import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, FileSpreadsheet, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const CHF = (n) => new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(n || 0);
const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const STATUS = {
  sent: { icon: CheckCircle2, cls: 'text-green-600', label: 'Gesendet' },
  failed: { icon: Clock, cls: 'text-amber-600', label: 'Wiederholung geplant' },
  failed_giveup: { icon: AlertTriangle, cls: 'text-red-600', label: 'Fehlgeschlagen' },
};

export const StudioBonusAccountingCard = () => {
  const [{ year, month }, setPeriod] = useState(prevMonth());
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, lRes] = await Promise.all([
        axios.get(`${API}/studio-bonus/accounting-preview?year=${year}&month=${month}`, authHeader()),
        axios.get(`${API}/studio-bonus/accounting-log?limit=20`, authHeader()),
      ]);
      setPreview(pRes.data);
      setLog(lRes.data.entries || []);
    } catch {
      toast.error('Buchhaltungs-Vorschau konnte nicht geladen werden');
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta) => setPeriod((p) => {
    const d = new Date(p.year, p.month - 1 + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });

  const send = async () => {
    if (!window.confirm(`GF-Boni für ${MONTHS_DE[month - 1]} ${year} an die Buchhaltung senden?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/studio-bonus/send-to-accounting?year=${year}&month=${month}`, {}, authHeader());
      if (r.data.note === 'erp_not_configured' || r.data.warning) {
        toast.warning('ERP nicht konfiguriert — es wurde nichts gesendet.');
      } else if (r.data.failed > 0) {
        toast.warning(`${r.data.sent} gesendet, ${r.data.failed} fehlgeschlagen (Wiederholung geplant).`);
      } else {
        toast.success(`${r.data.sent} Bonus-Buchung(en) an die Buchhaltung gesendet.`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const entries = preview?.entries || [];
  const configured = preview?.erp_configured;

  return (
    <Card className="border-indigo-200" data-testid="bonus-accounting-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-indigo-600" /> An die Buchhaltung übermitteln
            </CardTitle>
            <CardDescription>
              GF-Boni werden am 1. des Monats automatisch für den Vormonat an das ERP gesendet
              (idempotent). Hier kannst du sie vorab prüfen und manuell auslösen.
            </CardDescription>
          </div>
          <Badge className={configured ? 'bg-green-600' : 'bg-gray-400'} data-testid="bonus-accounting-erp-status">
            {configured ? 'ERP verbunden' : 'ERP nicht konfiguriert'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="bonus-accounting-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold min-w-[140px] text-center" data-testid="bonus-accounting-period">
              {MONTHS_DE[month - 1]} {year}
            </span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="bonus-accounting-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-indigo-700" data-testid="bonus-accounting-total">{CHF(preview?.total_chf)}</p>
              <p className="text-xs text-gray-500">{entries.length} Buchung(en)</p>
            </div>
            <Button onClick={send} disabled={sending || entries.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="bonus-accounting-send">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sendet…' : 'Jetzt senden'}
            </Button>
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="bonus-accounting-empty">
            Für diesen Monat gibt es keine Bonus-Buchungen (keine zugewiesenen GF oder kein Bonus).
          </p>
        ) : (
          <div className="border border-gray-100 rounded-lg divide-y">
            {entries.map((e) => (
              <div key={e.external_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`bonus-accounting-entry-${e.user_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">
                    {e.gf_name} <span className="text-[11px] text-gray-400">· {e.role === 'stv' ? 'Stv.-GF' : 'GF'}</span>
                  </p>
                  <p className="text-[11px] text-gray-500">{e.studio_name} · Umsatz {CHF(e.revenue)} · {e.bonus_percent}%</p>
                </div>
                <span className="font-semibold text-indigo-700 whitespace-nowrap">{CHF(e.personal_bonus_chf)}</span>
              </div>
            ))}
          </div>
        )}

        {log.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 mb-2">Letzte Übermittlungen</p>
            <div className="space-y-1 max-h-48 overflow-auto">
              {log.map((l) => {
                const st = STATUS[l.status] || { icon: Clock, cls: 'text-gray-400', label: l.status };
                const Icon = st.icon;
                return (
                  <div key={l.external_id} className="flex items-center justify-between text-xs px-1 py-1" data-testid={`bonus-accounting-log-${l.external_id}`}>
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Icon className={`w-3.5 h-3.5 ${st.cls}`} />
                      <span className="text-gray-600 truncate">{l.gf_name || l.user_id} · {l.period} · {l.studio_name}</span>
                    </span>
                    <span className={`${st.cls} whitespace-nowrap`}>{st.label}{l.error ? ` (${String(l.error).slice(0, 40)})` : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
